/**
 * Job applications (Recruitment -> Job Applications): the words of each
 * Philippines application form in one object per vacancy, so HR can edit them
 * and the edited copy (recruitment_job_application_settings.definition) is
 * layered over the code defaults. Client-safe: no DB access.
 *
 * Only words change through the editor: question labels, help texts,
 * placeholders, notes, upload labels and hints, section titles, the audio
 * scripts and instructions, the consent statements and the public page texts
 * (job title, tagline, introduction, "what to have ready"). The structure is
 * fixed: field names, types, options (they are the stored values the IT rules
 * and reports read), required rules, conditions, file kinds and limits all
 * come from the code definitions, and the sanitizer copies nothing else.
 *
 *   agent, client_care - intl-application.ts (STEP_FIELDS, audio parts,
 *               consent); the same form, worded separately
 *   marketing - marketing-application.ts (MARKETING_STEP_ITEMS, one recorded
 *               answer, consent, privacy notice link)
 */
import {
  INTL_AUDIO_GENERAL_INSTRUCTIONS,
  INTL_AUDIO_PARTS,
  INTL_CONSENT_ITEMS,
  INTL_DATA_STEPS,
  INTL_FILE_KINDS,
  INTL_FILE_LABELS,
  INTL_STEPS,
  INTL_STEP_TITLES,
  STEP_FIELDS,
  type IntlFieldDef,
} from '@/lib/recruitment/intl-application';
import {
  MARKETING_AUDIO_INSTRUCTIONS,
  MARKETING_AUDIO_PART,
  MARKETING_CONSENT_ITEMS,
  MARKETING_DATA_STEPS,
  MARKETING_PRIVACY_NOTICE_URL,
  MARKETING_STEPS,
  MARKETING_STEP_ITEMS,
  MARKETING_STEP_TITLES,
} from '@/lib/recruitment/marketing-application';
import {
  type FormStepItem,
  isField,
  isFileSlot,
  isNote,
} from '@/lib/recruitment/intl-form-engine';
import {
  INTL_VACANCIES,
  INTL_VACANCY_INFO,
  type AgentFormVacancy,
  type IntlVacancy,
} from '@/lib/recruitment/intl-vacancies';

export type VacancyPublicInfo = {
  jobTitle: string;
  tagline: string;
  intro: string;
  haveReady: string[];
};

export type AudioPartText = {
  key: string;
  title: string;
  instructions: string;
  scriptText: string | null;
  minSeconds: number;
  maxSeconds: number;
};

export type ConsentItemText = { key: string; text: string };

export type AgentFileText = { label: string; hint: string };

export type AgentFormDefinition = {
  vacancy: AgentFormVacancy;
  info: VacancyPublicInfo;
  stepTitles: Record<string, string>;
  /** Data steps only (the upload, audio, typing and consent steps have no fields). */
  stepFields: Record<string, IntlFieldDef[]>;
  files: Record<string, AgentFileText>;
  audioParts: AudioPartText[];
  audioInstructions: string[];
  consentItems: ConsentItemText[];
};

export type MarketingFormDefinition = {
  vacancy: 'marketing';
  info: VacancyPublicInfo;
  stepTitles: Record<string, string>;
  /** Data steps only (questions, upload slots and notes in order). */
  stepItems: Record<string, FormStepItem[]>;
  audioPart: AudioPartText;
  audioInstructions: string[];
  consentItems: ConsentItemText[];
  /** Empty = no link shown before the consent boxes. */
  privacyNoticeUrl: string;
};

export type JobApplicationDefinition = AgentFormDefinition | MarketingFormDefinition;

/** Hints of the agent form's upload cards (they used to live in the step components). */
const AGENT_FILE_HINTS: Record<string, string> = {
  gov_id: "A clear photo of your passport, driver's license, UMID, or national ID.",
  selfie_with_id: 'A selfie of you holding the same ID next to your face.',
  specs: 'A screenshot of the computer specifications: OS, processor, and RAM. JPG, PNG, or PDF.',
  specs_backup: 'A screenshot of the backup device specifications: OS, processor, and RAM. JPG, PNG, or PDF.',
  speedtest: 'Run a test at speedtest.net and upload a screenshot of the result.',
  resume: 'Upload your most recent resume or CV as a PDF or Word document.',
};

function publicInfo(vacancy: IntlVacancy): VacancyPublicInfo {
  const info = INTL_VACANCY_INFO[vacancy];
  return {
    jobTitle: info.jobTitle,
    tagline: info.tagline,
    intro: info.intro,
    haveReady: [...info.haveReady],
  };
}

export const DEFAULT_AGENT_DEFINITION: AgentFormDefinition = {
  vacancy: 'agent',
  info: publicInfo('agent'),
  stepTitles: Object.fromEntries(INTL_STEPS.map((s) => [s, INTL_STEP_TITLES[s]])),
  stepFields: Object.fromEntries(INTL_DATA_STEPS.map((s) => [s, STEP_FIELDS[s]])),
  files: Object.fromEntries(
    INTL_FILE_KINDS.map((k) => [k, { label: INTL_FILE_LABELS[k], hint: AGENT_FILE_HINTS[k] ?? '' }]),
  ),
  audioParts: INTL_AUDIO_PARTS.map((p) => ({ ...p })),
  audioInstructions: [...INTL_AUDIO_GENERAL_INSTRUCTIONS],
  consentItems: INTL_CONSENT_ITEMS.map((c) => ({ ...c })),
};

export const DEFAULT_MARKETING_DEFINITION: MarketingFormDefinition = {
  vacancy: 'marketing',
  info: publicInfo('marketing'),
  stepTitles: Object.fromEntries(MARKETING_STEPS.map((s) => [s, MARKETING_STEP_TITLES[s]])),
  stepItems: Object.fromEntries(MARKETING_DATA_STEPS.map((s) => [s, MARKETING_STEP_ITEMS[s]])),
  audioPart: { ...MARKETING_AUDIO_PART },
  audioInstructions: [...MARKETING_AUDIO_INSTRUCTIONS],
  consentItems: MARKETING_CONSENT_ITEMS.map((c) => ({ ...c })),
  privacyNoticeUrl: MARKETING_PRIVACY_NOTICE_URL,
};

export const DEFAULT_AGENT_FORM_DEFINITIONS: Record<AgentFormVacancy, AgentFormDefinition> = {
  agent: DEFAULT_AGENT_DEFINITION,
  client_care: {
    ...DEFAULT_AGENT_DEFINITION,
    vacancy: 'client_care',
    info: publicInfo('client_care'),
  },
};

export const DEFAULT_JOB_APPLICATION_DEFINITIONS: Record<IntlVacancy, JobApplicationDefinition> = {
  ...DEFAULT_AGENT_FORM_DEFINITIONS,
  marketing: DEFAULT_MARKETING_DEFINITION,
};

export function defaultJobApplicationDefinition(vacancy: IntlVacancy): JobApplicationDefinition {
  return DEFAULT_JOB_APPLICATION_DEFINITIONS[vacancy];
}

// ---------------------------------------------------------------------------
// Sanitizer: texts from the input over the default structure
// ---------------------------------------------------------------------------

const obj = (raw: unknown): Record<string, unknown> =>
  raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};

const list = (raw: unknown): unknown[] => (Array.isArray(raw) ? raw : []);

/** A non-empty string from the input, else the default; trimmed and capped. */
const text = (raw: unknown, fallback: string, max = 4000): string =>
  typeof raw === 'string' && raw.trim() ? raw.trim().slice(0, max) : fallback;

/** An optional text: '' clears it, a missing or wrong value keeps the default. */
const optText = (raw: unknown, fallback: string | undefined, max = 4000): string | undefined => {
  if (raw === undefined) return fallback;
  if (typeof raw !== 'string') return fallback;
  const v = raw.trim();
  return v ? v.slice(0, max) : undefined;
};

const lines = (raw: unknown, fallback: string[]): string[] => {
  if (!Array.isArray(raw)) return fallback;
  const out = raw
    .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
    .map((v) => v.trim().slice(0, 1000));
  return out;
};

function info(raw: unknown, def: VacancyPublicInfo): VacancyPublicInfo {
  const r = obj(raw);
  return {
    jobTitle: text(r.jobTitle, def.jobTitle, 200),
    tagline: text(r.tagline, def.tagline, 200),
    intro: text(r.intro, def.intro),
    haveReady: 'haveReady' in r ? lines(r.haveReady, def.haveReady) : def.haveReady,
  };
}

function titles(raw: unknown, def: Record<string, string>): Record<string, string> {
  const r = obj(raw);
  return Object.fromEntries(Object.entries(def).map(([k, v]) => [k, text(r[k], v, 200)]));
}

function audioPart(raw: unknown, def: AudioPartText): AudioPartText {
  const r = obj(raw);
  return {
    key: def.key,
    title: text(r.title, def.title, 200),
    instructions: text(r.instructions, def.instructions),
    scriptText: def.scriptText === null ? null : text(r.scriptText, def.scriptText),
    minSeconds: def.minSeconds,
    maxSeconds: def.maxSeconds,
  };
}

function consent(raw: unknown, def: ConsentItemText[]): ConsentItemText[] {
  const given = list(raw);
  return def.map((c) => ({
    key: c.key,
    text: text(obj(given.find((g) => obj(g).key === c.key)).text, c.text, 1000),
  }));
}

function agentFields(raw: unknown, def: IntlFieldDef[]): IntlFieldDef[] {
  const given = list(raw);
  return def.map((f) => {
    const g = obj(given.find((x) => obj(x).column === f.column));
    return {
      ...f,
      label: text(g.label, f.label, 300),
      help: optText(g.help, f.help, 1000),
      placeholder: optText(g.placeholder, f.placeholder, 200),
    };
  });
}

function marketingItems(raw: unknown, def: FormStepItem[]): FormStepItem[] {
  const given = list(raw);
  return def.map((item, index) => {
    if (isField(item)) {
      const g = obj(given.find((x) => isField(x as FormStepItem) && obj(x).name === item.name));
      return {
        ...item,
        label: text(g.label, item.label, 500),
        help: optText(g.help, item.help, 1000),
        placeholder: optText(g.placeholder, item.placeholder, 200),
      };
    }
    if (isFileSlot(item)) {
      const g = obj(given.find((x) => isFileSlot(x as FormStepItem) && obj(x).file === item.file));
      return {
        ...item,
        label: text(g.label, item.label, 300),
        hint: optText(g.hint, item.hint, 500),
      };
    }
    if (isNote(item)) {
      // Notes have no key: matched by their position in the step. Their
      // condition and heading style stay as in the code.
      const g = obj(given[index]);
      return { ...item, note: text(g.note, item.note, 1000) };
    }
    return item;
  });
}

function privacyUrl(raw: unknown, fallback: string): string {
  if (typeof raw !== 'string') return fallback;
  const v = raw.trim();
  if (!v) return '';
  return /^https?:\/\/\S+$/i.test(v) ? v.slice(0, 500) : fallback;
}

/**
 * An edited definition reduced to the texts the editor may change, on top of
 * the default structure of the vacancy. Null when the input is not an object.
 */
export function sanitizeJobApplicationDefinition(
  vacancy: IntlVacancy,
  input: unknown,
): JobApplicationDefinition | null {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return null;
  const r = input as Record<string, unknown>;
  if (vacancy !== 'marketing') {
    const d = DEFAULT_AGENT_FORM_DEFINITIONS[vacancy];
    const fields = obj(r.stepFields);
    const files = obj(r.files);
    const parts = list(r.audioParts);
    return {
      vacancy,
      info: info(r.info, d.info),
      stepTitles: titles(r.stepTitles, d.stepTitles),
      stepFields: Object.fromEntries(
        Object.entries(d.stepFields).map(([step, defs]) => [step, agentFields(fields[step], defs)]),
      ),
      files: Object.fromEntries(
        Object.entries(d.files).map(([kind, f]) => {
          const g = obj(files[kind]);
          return [kind, { label: text(g.label, f.label, 200), hint: optText(g.hint, f.hint, 500) ?? '' }];
        }),
      ),
      audioParts: d.audioParts.map((p) => audioPart(parts.find((g) => obj(g).key === p.key), p)),
      audioInstructions: 'audioInstructions' in r ? lines(r.audioInstructions, d.audioInstructions) : d.audioInstructions,
      consentItems: consent(r.consentItems, d.consentItems),
    };
  }
  const d = DEFAULT_MARKETING_DEFINITION;
  const items = obj(r.stepItems);
  return {
    vacancy: 'marketing',
    info: info(r.info, d.info),
    stepTitles: titles(r.stepTitles, d.stepTitles),
    stepItems: Object.fromEntries(
      Object.entries(d.stepItems).map(([step, defs]) => [step, marketingItems(items[step], defs)]),
    ),
    audioPart: audioPart(r.audioPart, d.audioPart),
    audioInstructions: 'audioInstructions' in r ? lines(r.audioInstructions, d.audioInstructions) : d.audioInstructions,
    consentItems: consent(r.consentItems, d.consentItems),
    privacyNoticeUrl: privacyUrl(r.privacyNoticeUrl, d.privacyNoticeUrl),
  };
}

/** Questions (fields) and upload slots of a definition, for the list card. */
export function jobApplicationCounts(def: JobApplicationDefinition): { steps: number; questions: number; uploads: number } {
  if (def.vacancy !== 'marketing') {
    return {
      steps: Object.keys(def.stepTitles).length,
      questions: Object.values(def.stepFields).reduce((n, fields) => n + fields.length, 0),
      uploads: Object.keys(def.files).length,
    };
  }
  const all = Object.values(def.stepItems).flat();
  return {
    steps: Object.keys(def.stepTitles).length,
    questions: all.filter(isField).length,
    uploads: all.filter(isFileSlot).length,
  };
}

export function isIntlVacancy(value: unknown): value is IntlVacancy {
  return typeof value === 'string' && (INTL_VACANCIES as readonly string[]).includes(value);
}
